import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { categoryLabel } from "@/data/portfolioCategories";

export default function LightboxModal({ project, onClose }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [project]);

  const images = project?.images || [];
  const total = images.length;

  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);

  useEffect(() => {
    if (!project) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [project, onClose, prev, next]);

  if (!project) return null;
  const current = images[idx];

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] bg-br-black/95 backdrop-blur-sm flex flex-col"
      data-testid="portfolio-lightbox"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-5 lg:px-10 py-4 border-b border-br-line">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze-2 font-bold">{categoryLabel(project.category)}</div>
          <h3 className="font-display text-2xl tracking-widest uppercase text-white truncate">{project.title}</h3>
          {project.city && (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-300">
              <MapPin size={12} className="text-br-bronze-2" /> {project.city}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-300 hover:text-white p-2 -mr-2"
          aria-label="Close gallery"
          data-testid="lightbox-close"
        >
          <X size={24} />
        </button>
      </div>

      {/* Image stage */}
      <div className="flex-1 relative flex items-center justify-center px-3 lg:px-12 py-5 overflow-hidden">
        {current ? (
          <img
            src={current.data_url}
            alt={current.alt || project.title}
            className="max-h-full max-w-full object-contain rounded shadow-2xl"
            data-testid="lightbox-image"
          />
        ) : (
          <div className="text-gray-400">No images</div>
        )}

        {current?.is_before && (
          <div className="absolute top-3 left-4 lg:left-14 bg-amber-500 text-white text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded">
            Before
          </div>
        )}
        {project.is_before_after && current && !current.is_before && (
          <div className="absolute top-3 left-4 lg:left-14 bg-emerald-600 text-white text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded">
            After
          </div>
        )}

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              className="absolute left-2 lg:left-6 top-1/2 -translate-y-1/2 bg-br-charcoal/80 hover:bg-br-bronze text-white p-2 rounded-full transition"
              aria-label="Previous image"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              onClick={next}
              className="absolute right-2 lg:right-6 top-1/2 -translate-y-1/2 bg-br-charcoal/80 hover:bg-br-bronze text-white p-2 rounded-full transition"
              aria-label="Next image"
              data-testid="lightbox-next"
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 lg:px-10 py-4 border-t border-br-line bg-br-charcoal/60">
        {project.description && (
          <p className="text-sm text-gray-300 leading-relaxed max-w-3xl">{project.description}</p>
        )}
        {total > 1 && (
          <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1">
            {images.map((img, i) => (
              <button
                key={img.id || i}
                type="button"
                onClick={() => setIdx(i)}
                className={`h-14 w-20 shrink-0 rounded overflow-hidden border-2 transition ${i === idx ? "border-br-bronze" : "border-transparent opacity-70 hover:opacity-100"}`}
                aria-label={`Image ${i + 1}`}
              >
                <img src={img.data_url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <div className="mt-3 text-[11px] uppercase tracking-[0.25em] text-gray-400">
          Image {Math.min(idx + 1, total)} of {total}
        </div>
      </div>
    </div>
  );
}
